import React, { useRef } from 'react';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { GridSection } from '../../../Episode.Styled';
import { PrintButtonHeader } from '../../2284Tab/2284Dialog/Pdf2284CommonUtils';
import { getValidDate } from '../../../../common/commonUtils/commonUtils';
import { CostTableRow } from './TransportCostTable';
import {
  HeaderGrid,
  ParticipantInformation,
  ContactsGridSection,
  ScheduleGridSection,
  MappedPlanCompletedVal,
  GridSectionData,
  TransportationCheckBox,
  GetHeading,
} from './transportPdfCommonUtils';
import {
  getApprovedCheckbox,
  getContactsData,
  getContactsFirstData,
  getContactsSecondData,
  getFailedToAppearCheckbox,
  getFirstRowData,
  getParticipantInfo,
  getPhoneFaxData,
  getPlanApprovedData,
  getPlanCompletedData,
  getScheduleData,
  outcomeTitle,
  planApprovedNote1,
  planApprovedNote2,
} from './transportPdfConstants';

/**
 * Prepare all data needed for the PDF from Redux state
 */
const preparePdfData = (transportForm, transportPdfData) => {
  const {
    initialDepartureDate,
    departingSAPorJailDepartureLocationName = '',
    source = '',
    fundingSTOPContact = '',
    fundingSTOPBilltoOrganizationName = '',
    specialTransport,
    outcome = '',
  } = transportForm || {};

  const {
    trnID = '',
    epdID = '',
    epdIcnCDC = '',
    prtKey = '',
    toOrg = '',
    trnDateCreate,
    trnHiCon,
    trnCmt = '',
    toContact,
    fromContact,
  } = transportPdfData || {};

  // TODO: These should come from actual data, not hardcoded
  const approved = false;
  const failedToAppear = false;

  // Format dates
  const departureDate = getValidDate(initialDepartureDate);
  const formattedFundingSTOPContact = getValidDate(fundingSTOPContact);
  const formattedTrnDateCreate = getValidDate(trnDateCreate);

  // Prepare data structures for each section
  const firstData = getFirstRowData({ source, trnHiCon, specialTransport });
  const secondData = getContactsSecondData({
    fundingSTOPContact: formattedFundingSTOPContact,
    fromContact
  });
  const contactsFirstData = getContactsFirstData({
    fundingSTOPBilltoOrganizationName,
    departingSAPorJailDepartureLocationName,
    toOrg,
    toContact,
  });

  return {
    // Header data
    trnID,
    epdID,

    // Participant information
    participantInfo: getParticipantInfo({
      prtKey,
      epdIcnCDC,
      departingSAPorJailDepartureLocationName,
      firstData,
      trnCmt,
    }),

    // Contacts section
    contactsData: getContactsData({
      contactsFirstData,
      secondData,
      getPhoneFaxData,
    }),

    // Schedule section
    scheduleData: getScheduleData({ departureDate }),

    // Plan Completed section
    approvedCheckBox: getApprovedCheckbox({ approved }),
    appearCheckBox: getFailedToAppearCheckbox({ failedToAppear }),
    outcome,

    // Plan Approved section
    approveData: getPlanApprovedData({ getTrnDateCreate: formattedTrnDateCreate }),
  };
};

const TransportPdf = (props) => {
  const { open, setOpen } = props;

  const transportPdfData = useSelector((state) => state?.transportPdfForm);
  const transportForm = useSelector((state) => state?.transportForm);

  const componentRef = useRef();

  const handleClose = () => {
    setOpen(false);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  // Prepare all PDF data
  const pdfData = preparePdfData(transportForm, transportPdfData);

  return (
    <Dialog fullScreen open={open} onClose={setOpen}>
      <PrintButtonHeader handlePrint={handlePrint} handleClose={handleClose} />
      <Grid container ref={componentRef}>
        <GridSection mt={1} mx={2} container spacing={1}>
          <HeaderGrid
            leftTitle1={`Trn ID : ${pdfData.trnID}`}
            leftTitle2={`Epd ID : ${pdfData.epdID}`}
            middleHeading="STOP TRANSPORTATION PLAN"
            rightTitle1="Last Revised Dec 2014"
          />
        </GridSection>

        <ParticipantInformation item={pdfData.participantInfo} />
        <ContactsGridSection heading="CONTACTS" item={pdfData.contactsData} />
        <ScheduleGridSection heading="SCHEDULE" item={pdfData.scheduleData} />

        <GridSection mt={1} mx={2} container spacing={1}>
          <GetHeading heading="Plan Completed" />
          <TransportationCheckBox item={pdfData.approvedCheckBox} />
          <Grid item xs={4}>
            {outcomeTitle} {pdfData.outcome}
          </Grid>
          <TransportationCheckBox item={pdfData.appearCheckBox} />
          <MappedPlanCompletedVal item={getPlanCompletedData()} />
        </GridSection>

        <CostTableRow />

        <GridSection mt={1} mx={2} container spacing={1}>
          <GridSectionData item={pdfData.approveData} heading="Plan Approved" />
          <Grid item xs={3}>
            {planApprovedNote1}
          </Grid>
          <Grid item xs={9}>
            {planApprovedNote2}
          </Grid>
        </GridSection>
      </Grid>
    </Dialog>
  );
};

export default TransportPdf;
